/**
 * FICHA DA FATURA (modal de visualização em Financeiro > Faturas).
 * Prévia aprovada: https://claude.ai/artifact/3MMLW4wr53hQ5KSEaZTuiB
 *
 * O mesmo tratamento que a ficha da assinatura ganhou (#199): números na
 * frente, descrição comercial a um clique, e só o que importa para AQUELA
 * fatura. Leituras puras para a faixa e para a seção de empenho.
 */

export interface SituacaoDoBoleto {
  texto: string;
  tom: 'ok' | 'aviso' | 'neutro' | 'erro';
  dica: string;
}

const METODO: Record<string, string> = { pix: 'PIX', ted: 'TED', deposito: 'Depósito', 'depósito': 'Depósito' };

/**
 * A situação da cobrança numa palavra. Título da API tem PIX e status
 * Confirmado (⚡); título de remessa fica Registrado e sem PIX (ver skill de
 * faturamento, 26/08/2026).
 */
export function situacaoDoBoleto(inv: { paymentMethod?: string | null; nossoNumero?: string | null; boletoStatus?: string | null }): SituacaoDoBoleto {
  const metodo = String(inv.paymentMethod || '').toLowerCase();
  if (metodo && metodo !== 'boleto') {
    const nome = METODO[metodo] || inv.paymentMethod || '—';
    return { texto: nome, tom: 'neutro', dica: `Cobrança por ${nome}, sem boleto` };
  }
  if (!inv.nossoNumero) return { texto: 'não emitido', tom: metodo === 'boleto' ? 'aviso' : 'neutro', dica: 'Nenhum título no banco para esta fatura' };
  const st = String(inv.boletoStatus || '').toLowerCase();
  if (st === 'confirmado') return { texto: '⚡ Confirmado', tom: 'ok', dica: `Título ${inv.nossoNumero} registrado pela API, com PIX` };
  if (st === 'pago') return { texto: 'Pago', tom: 'ok', dica: `Título ${inv.nossoNumero} liquidado` };
  if (st === 'registrado') return { texto: 'Registrado', tom: 'neutro', dica: `Título ${inv.nossoNumero} de remessa, sem PIX` };
  if (st === 'baixado') return { texto: 'Baixado', tom: 'aviso', dica: `Título ${inv.nossoNumero} baixado no banco` };
  if (st === 'rejeitado') return { texto: 'Rejeitado', tom: 'erro', dica: `Título ${inv.nossoNumero} rejeitado pelo banco` };
  return { texto: st || 'emitido', tom: 'neutro', dica: `Título ${inv.nossoNumero}` };
}

/** "01/10 a 31/10" (mesmo ano) ou "15/12/25 a 14/01/26". `curta` = fmtShortDate. */
export function periodoDeServico(inicio: string | null | undefined, fim: string | null | undefined, curta: (d: any) => string): string {
  if (!inicio && !fim) return '—';
  if (!inicio || !fim) return curta(inicio || fim);
  const a = curta(inicio);
  const b = curta(fim);
  const mesmoAno = String(inicio).slice(0, 4) === String(fim).slice(0, 4);
  return mesmoAno ? `${a.slice(0, 5)} a ${b.slice(0, 5)}` : `${a} a ${b}`;
}

/**
 * A linha de registro de empenho só serve a ÓRGÃO PÚBLICO. Ocupava 75px em
 * toda fatura, inclusive de cliente privado, e era um terço do que estourava
 * a altura e cortava os botões do modal (03/10/2026). Fatura privada que já
 * tem empenho registrado continua mostrando o campo: não se esconde dado.
 */
export const mostraCampoDeEmpenho = (publico: boolean, empenhoNumber?: string | null) => publico || !!String(empenhoNumber || '').trim();

const soData = (d: unknown) => String(d || '').split('T')[0];

/** Dias corridos do período, inclusivos (01 a 31 = 31). 0 se faltar data. Sem fuso: conta pelo calendário. */
export function diasNoPeriodo(inicio: string | null | undefined, fim: string | null | undefined): number {
  const a = soData(inicio);
  const b = soData(fim);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(a) || !/^\d{4}-\d{2}-\d{2}$/.test(b)) return 0;
  const [ya, ma, da] = a.split('-').map(Number);
  const [yb, mb, db] = b.split('-').map(Number);
  return Math.round((Date.UTC(yb, mb - 1, db) - Date.UTC(ya, ma - 1, da)) / 86400000) + 1;
}

/**
 * Itens cujo período próprio difere do período da fatura. O item pode ter o seu
 * (proporcional de quem entrou no meio do mês), e a divergência ficava em silêncio
 * lá dentro; o cabeçalho do editor agora a mostra.
 */
export function itensComPeriodoProprio(
  items: Array<{ periodStart?: string | null; periodEnd?: string | null }> | null | undefined,
  inicio: string | null | undefined,
  fim: string | null | undefined,
): number {
  const a = soData(inicio);
  const b = soData(fim);
  return (items || []).filter((i) => (i.periodStart && soData(i.periodStart) !== a) || (i.periodEnd && soData(i.periodEnd) !== b)).length;
}
